// Shared Revenue Cycle Management (RCM) helpers — status metadata, money
// formatting, and a printable superbill generator.

export const CLAIM_STATUS_META = {
  draft: { label: 'Draft', cls: 'bg-cn-bone-200 text-cn-text-muted border-cn-border' },
  submitted: { label: 'Submitted', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  in_review: { label: 'In review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  partially_paid: { label: 'Partially paid', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  paid: { label: 'Paid', cls: 'bg-cn-sage-100 text-cn-primary border-cn-sage-200' },
  denied: { label: 'Denied', cls: 'bg-red-50 text-red-700 border-red-200' },
  appealed: { label: 'Appealed', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  void: { label: 'Void', cls: 'bg-gray-100 text-gray-500 border-gray-200 line-through' },
};

export const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const POS_LABEL = {
  '12': 'Home', '02': 'Telehealth', '11': 'Office / Clinic',
  '13': 'Assisted living', '33': 'Custodial care',
};

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/** Open a clean, printable CMS-1500-style superbill in a new window. */
export const openSuperbill = (claim) => {
  if (!claim) return;
  const lines = (claim.service_lines || []).map((l) => `
    <tr>
      <td>${esc(l.cpt_code)}${l.modifier ? ` <span class="mod">${esc(l.modifier)}</span>` : ''}</td>
      <td>${esc(l.description || '')}</td>
      <td class="r">${Number(l.units || 0)}</td>
      <td class="r">$${Number(l.charge || 0).toFixed(2)}</td>
      <td class="r">$${Number(l.line_total || (l.units * l.charge) || 0).toFixed(2)}</td>
    </tr>`).join('');
  const dx = (claim.diagnosis_codes || []).map((d) => `${esc(d.code)}${d.description ? ` — ${esc(d.description)}` : ''}`).join('<br/>') || '—';
  const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Superbill ${esc(claim.claim_number)}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #1d2b27; margin: 0; padding: 40px; }
    .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #1E293B; padding-bottom:16px; }
    .brand { font-size:26px; font-weight:800; color:#1E293B; letter-spacing:-0.5px; }
    .doc { text-align:right; }
    .doc h1 { font-size:18px; margin:0; }
    .doc .num { color:#6b7770; font-family:monospace; font-size:13px; }
    .grid { display:grid; grid-template-columns:1fr 1fr; gap:24px; margin:24px 0; }
    .box h3 { font-size:10px; text-transform:uppercase; letter-spacing:1px; color:#6b7770; margin:0 0 6px; }
    .box p { margin:2px 0; font-size:14px; }
    table { width:100%; border-collapse:collapse; margin-top:8px; }
    th { text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:.6px; color:#6b7770; border-bottom:2px solid #e4e0d8; padding:8px 6px; }
    td { padding:9px 6px; font-size:13px; border-bottom:1px solid #efece5; }
    td.r, th.r { text-align:right; }
    .mod { background:#f0ede6; padding:1px 4px; border-radius:4px; font-size:11px; }
    .totals { margin-top:18px; margin-left:auto; width:300px; }
    .totals div { display:flex; justify-content:space-between; padding:5px 0; font-size:14px; }
    .totals .due { border-top:2px solid #1E293B; margin-top:6px; padding-top:10px; font-weight:800; font-size:16px; color:#1E293B; }
    .note { margin-top:28px; font-size:11px; color:#8a938d; border-top:1px solid #efece5; padding-top:12px; }
    @media print { body { padding:24px; } .noprint { display:none; } }
  </style></head><body>
    <div class="head">
      <div><div class="brand">CaraLoom</div><div style="font-size:11px;color:#6b7770;">Statement / Superbill for insurance submission</div></div>
      <div class="doc"><h1>SUPERBILL</h1><div class="num">${esc(claim.claim_number)}</div>
        <div style="font-size:12px;margin-top:4px;">Date of service: ${esc(claim.date_of_service)}</div></div>
    </div>
    <div class="grid">
      <div class="box"><h3>Provider</h3><p><b>${esc(claim.clinician_name)}</b></p></div>
      <div class="box"><h3>Patient</h3><p><b>${esc(claim.patient_name)}</b></p>${claim.patient_dob ? `<p>DOB: ${esc(claim.patient_dob)}</p>` : ''}</div>
      <div class="box"><h3>Payer</h3>${claim.billing_type === 'self_pay' ? '<p>Self-pay</p>' : `<p>${esc(claim.payer_name || '—')}</p>${claim.member_id ? `<p>Member ID: ${esc(claim.member_id)}</p>` : ''}${claim.group_number ? `<p>Group: ${esc(claim.group_number)}</p>` : ''}`}</div>
      <div class="box"><h3>Place of service</h3><p>${esc(POS_LABEL[claim.place_of_service] || claim.place_of_service)}</p><h3 style="margin-top:10px;">Diagnoses (ICD-10)</h3><p>${dx}</p></div>
    </div>
    <table>
      <thead><tr><th>CPT / HCPCS</th><th>Description</th><th class="r">Units</th><th class="r">Charge</th><th class="r">Total</th></tr></thead>
      <tbody>${lines}</tbody>
    </table>
    <div class="totals">
      <div><span>Total charges</span><span>$${Number(claim.total_charges || 0).toFixed(2)}</span></div>
      <div><span>Insurance paid</span><span>-$${Number(claim.insurance_paid || 0).toFixed(2)}</span></div>
      <div><span>Adjustments</span><span>-$${Number(claim.adjustments || 0).toFixed(2)}</span></div>
      <div><span>Patient paid</span><span>-$${Number(claim.patient_paid || 0).toFixed(2)}</span></div>
      <div class="due"><span>Balance due</span><span>$${Number(claim.patient_balance ?? claim.balance ?? 0).toFixed(2)}</span></div>
    </div>
    <div class="note">CaraLoom is a technology marketplace; clinicians are independent providers. This superbill may be submitted to your insurance for reimbursement. Generated ${new Date().toLocaleDateString()}.</div>
    <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
  </body></html>`;
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); }
};
